import { createSelector } from 'reselect';
import { compact } from 'lodash';
import { UNKNOWN_COUNTRY_CODE } from 'constants/country';
import { transformAddress } from 'data/transformers/address';
import { stateName } from './constants';

const localState = state => state[stateName];

export const getUser = state => localState(state).user;
export const getUserId = state => {
  const user = getUser(state);
  if (!user) return null;
  return user.id;
};
export const getUserExternalId = state => {
  var _getUser;

  return ((_getUser = getUser(state)) === null || _getUser === void 0 ? void 0 : _getUser.external_id) || null;
};
export const getUserEmail = state => {
  const user = getUser(state);
  if (!user) return null;
  return user.email;
};
export const getUserCountryCode = createSelector(getUser, user => user === null || user === void 0 ? void 0 : user.country_code);
export const getUserFavouriteTopicCount = createSelector(getUser, user => user === null || user === void 0 ? void 0 : user.favourite_topic_count);
export const getUserForumTopicCount = createSelector(getUser, user => user === null || user === void 0 ? void 0 : user.forum_topic_count);
export const getUserCountryIsoCode = createSelector(getUser, user => user === null || user === void 0 ? void 0 : user.country_iso_code);
export const getUserRealName = createSelector(getUser, user => user === null || user === void 0 ? void 0 : user.real_name);
export const getUserDefaultAddress = createSelector(getUser, user => (user === null || user === void 0 ? void 0 : user.default_address) && transformAddress(user.default_address));
export const getFormattedAddress = createSelector(getUserDefaultAddress, address => compact([address === null || address === void 0 ? void 0 : address.line1, address === null || address === void 0 ? void 0 : address.line2, address === null || address === void 0 ? void 0 : address.city, address === null || address === void 0 ? void 0 : address.state, address === null || address === void 0 ? void 0 : address.postalCode]).join(' '));
export const getUserRegistrationDate = createSelector(getUser, user => (user === null || user === void 0 ? void 0 : user.created_at) && new Date(user.created_at));
export const getCityTitle = state => {
  const user = getUser(state);
  if (!user) return null;
  return user.city;
};
export const getCityId = state => {
  const user = getUser(state);
  if (!user) return null;
  return user.city_id;
};
export const getCountryId = state => {
  const user = getUser(state);
  if (!user) return null;
  return user.country_id;
};
export const getCountryTitle = createSelector(getUser, user => user === null || user === void 0 ? void 0 : user.country_title);
export const getIsLocationPublic = state => {
  const user = getUser(state);
  return !!(user !== null && user !== void 0 && user.is_location_public);
};
export const getIsUserGod = state => {
  const user = getUser(state);
  return !!(user !== null && user !== void 0 && user.is_god);
};
export const getUserPhoto = state => {
  var _localState$user;

  return ((_localState$user = localState(state).user) === null || _localState$user === void 0 ? void 0 : _localState$user.photo) || null;
};
export const getAnonId = state => localState(state).anon_id;
export const getTrackingPlatform = state => localState(state).tracking_platform;
export const getTrackerDebugPin = state => localState(state).tracker_debug_pin;
export const getLanguageCode = state => localState(state).languageCode;
export const getIsUserPhoneValid = state => {
  var _getUser2;

  return (_getUser2 = getUser(state)) === null || _getUser2 === void 0 ? void 0 : _getUser2.verification.phone.valid;
};
export const getIsUserEmailValid = state => {
  var _getUser3;

  return (_getUser3 = getUser(state)) === null || _getUser3 === void 0 ? void 0 : _getUser3.verification.email.valid;
};
export const getIsUserRestrictedByTerms = state => {
  var _getUser4;

  return (_getUser4 = getUser(state)) === null || _getUser4 === void 0 ? void 0 : _getUser4.restricted_by_terms;
};
export const getUserItemCount = state => {
  var _getUser5;

  return ((_getUser5 = getUser(state)) === null || _getUser5 === void 0 ? void 0 : _getUser5.item_count) || 0;
};
export const getUserBundleDiscount = state => {
  var _getUser6;

  return (_getUser6 = getUser(state)) === null || _getUser6 === void 0 ? void 0 : _getUser6.bundle_discount;
};
export const getHasConfirmedPaymentsAccount = state => {
  var _getUser7;

  return (_getUser7 = getUser(state)) === null || _getUser7 === void 0 ? void 0 : _getUser7.has_confirmed_payments_account;
};
export const getIsFundraiserActive = state => {
  var _getUser8, _getUser8$fundraiser;

  return !!((_getUser8 = getUser(state)) !== null && _getUser8 !== void 0 && (_getUser8$fundraiser = _getUser8.fundraiser) !== null && _getUser8$fundraiser !== void 0 && _getUser8$fundraiser.active);
};
export const getFundraiserPercentage = state => {
  var _getUser9, _getUser9$fundraiser;

  return (_getUser9 = getUser(state)) === null || _getUser9 === void 0 ? void 0 : (_getUser9$fundraiser = _getUser9.fundraiser) === null || _getUser9$fundraiser === void 0 ? void 0 : _getUser9$fundraiser.percentage;
};
export const getIsCatalogModerator = state => {
  var _getUser10;

  return (_getUser10 = getUser(state)) === null || _getUser10 === void 0 ? void 0 : _getUser10.is_catalog_moderator;
};
export const getIsCatalogRoleMarketingPhotos = state => {
  var _getUser11;

  return (_getUser11 = getUser(state)) === null || _getUser11 === void 0 ? void 0 : _getUser11.is_catalog_role_marketing_photos;
};
export const getIsAuthenticated = state => !!getUser(state);
export const getPortalMergeSourceEnabled = state => localState(state).portalMergeSourceEnabled;
export const getBusinessAccount = state => {
  var _getUser12;

  return (_getUser12 = getUser(state)) === null || _getUser12 === void 0 ? void 0 : _getUser12.business_account;
};
export const getBusinessAccountId = state => {
  var _getBusinessAccount;

  return (_getBusinessAccount = getBusinessAccount(state)) === null || _getBusinessAccount === void 0 ? void 0 : _getBusinessAccount.id;
};
export const getIsBusinessAccount = state => {
  var _getUser13;

  return !!((_getUser13 = getUser(state)) !== null && _getUser13 !== void 0 && _getUser13.business);
};
export const getIsBpfPriceProminenceApplied = state => {
  var _getUser14;

  return (_getUser14 = getUser(state)) === null || _getUser14 === void 0 ? void 0 : _getUser14.is_bpf_price_prominence_applied;
}; // TODO: drop these 👇 selectors after we fully migrated to the next.js and use getBrazeConfigs

export const getSessionBrazeConfig = state => localState(state).braze; // TODO: drop these 👇 selectors after we fully migrated to the next.js and use next-env directly

export const getSessionFacebookAppId = state => localState(state).facebookAppId;
export const getSessionAppleClientId = state => localState(state).appleClientId;
export const getSessionGoogleClientId = state => localState(state).googleClientId;
export const getSessionGoogleMapsApiKey = state => localState(state).googleMapsApiKey;
export const getIsSmartAdServerAvailable = state => localState(state).isSmartAdServerAvailable;
export const getCountryCodeByIp = createSelector(localState, _ref => {
  let {
    countryCodeByIp
  } = _ref;
  return countryCodeByIp === UNKNOWN_COUNTRY_CODE ? undefined : countryCodeByIp;
});
export const getCountryCode = createSelector(localState, _ref2 => {
  let {
    countryCode
  } = _ref2;
  return countryCode;
});
export const getExposee = createSelector([getAnonId, getUserId, getCountryCode], (anonId, userId, countryCode) => ({
  anonId,
  userId,
  countryCode
}));
export const getIsAccountBanned = createSelector(getUser, user => !!(user !== null && user !== void 0 && user.account_ban_date));
export const getIsWebView = state => localState(state).isWebView;
export const getIsContentOnlyView = state => localState(state).isContentOnlyView;
export const getPlaform = state => localState(state).platform;